export const defaultBrief = {
  title: "Website Redesign Brief",
  intro: "We've put this together to help us understand your vision. It should take about 12 minutes.",
  outro: "The brief is in. We're already thinking.",
  sections: [
    {
      id: "why",
      title: "The Why",
      questions: [
        {
          id: "email",
          type: "text",
          required: true,
          label: "What's your email address?",
          help: "We'll send a copy of the completed brief here too."
        },
        {
          id: "q1",
          type: "longtext",
          required: true,
          label: "What's making you consider a redesign right now?",
          help: "The honest version helps us most. Bookings, slow site, hard to update, or just a gut feeling that the site no longer reflects the property."
        },
        {
          id: "q2",
          type: "longtext",
          required: false,
          label: "If you had to point to one moment where you thought the website needs to change, what was it?"
        },
        {
          id: "q3",
          type: "longtext",
          required: false,
          label: "What's working well on the current site that we should protect?"
        },
        {
          id: "q4",
          type: "text",
          required: true,
          label: "In one sentence, when a guest lands on the new site, what should they feel?"
        }
      ]
    },
    {
      id: "business",
      title: "Business & Audience",
      questions: [
        {
          id: "q5",
          type: "multi-select",
          required: true,
          label: "What does the new website need to do for the business? Pick all that apply.",
          options: [
            "Increase direct bookings",
            "Better showcase retreats & events",
            "Build credibility for group bookings",
            "Improve search visibility",
            "Make it easier for the team to update",
            "Tell the brand story more powerfully",
            "Other"
          ],
          hasOther: true
        },
        {
          id: "q6",
          type: "longtext",
          required: true,
          label: "Who is the primary guest you want this site to attract?",
          help: "Be specific. Age range, where they're from, what they're seeking, what kind of stay they tend to book."
        },
        {
          id: "q7",
          type: "longtext",
          required: false,
          label: "Are there any specific segments you want to grow into that you aren't reaching today?",
          help: "For example: international travelers, corporate retreats, longer stays, or wellness seekers."
        },
        {
          id: "q8",
          type: "longtext",
          required: true,
          label: "Who are 2 or 3 brands whose websites you genuinely admire?",
          help: "These don't need to be competitors. A short note on why helps us a lot."
        },
        {
          id: "q9",
          type: "longtext",
          required: false,
          label: "Are there any websites that you specifically don't want the new site to feel like?"
        }
      ]
    },
    {
      id: "tech",
      title: "The Current Site & Tech",
      questions: [
        {
          id: "q10",
          type: "scale",
          required: true,
          label: "On a scale of 1 to 10, how satisfied are you with the current website?",
          min: 1,
          max: 10,
          minLabel: "Not at all",
          maxLabel: "Completely"
        },
        {
          id: "q11",
          type: "longtext",
          required: true,
          label: "What are the top 3 things that frustrate you about the current website?",
          help: "List them in order of how much they bother you."
        },
        {
          id: "q12",
          type: "single-select",
          required: true,
          label: "Are you open to changing the underlying technology of the website?",
          options: [
            "Yes, open to whatever serves the business best",
            "Prefer to stay on the current platform if possible",
            "Not sure, would like your recommendation"
          ]
        },
        {
          id: "q13",
          type: "longtext",
          required: true,
          label: "How do guests currently book a stay, and how would you ideally want them to book on the new site?",
          help: "For example: direct booking, enquiry form then WhatsApp, or redirect to OTAs."
        },
        {
          id: "q14",
          type: "single-select",
          required: true,
          label: "Do you have an active booking engine or PMS we'd need to integrate?",
          options: [
            "Yes, and we want to keep it",
            "Yes, but open to switching",
            "No, open to recommendations",
            "Not sure / will discuss on a call"
          ]
        },
        {
          id: "q15",
          type: "text",
          required: false,
          label: "If yes to the above, please name the tools you currently use:"
        }
      ]
    },
    {
      id: "scope",
      title: "Scope & Content",
      questions: [
        {
          id: "q16",
          type: "single-select",
          required: true,
          label: "Roughly how many pages do you imagine the new site will have?",
          options: [
            "Small (5 to 8 pages)",
            "Medium (9 to 15 pages)",
            "Large (15+ pages)",
            "Not sure, please advise"
          ]
        },
        {
          id: "q17",
          type: "multi-select",
          required: true,
          label: "What stays the same?",
          options: [
            "Logo",
            "Brand colors",
            "Existing photography",
            "Existing copy",
            "Domain & hosting",
            "None of the above"
          ]
        },
        {
          id: "q18",
          type: "single-select",
          required: true,
          label: "Photography & video",
          options: [
            "We have a strong library we'll provide",
            "We have some, but a fresh shoot would help",
            "We'd like a new shoot organized as part of this project",
            "Not sure yet"
          ]
        },
        {
          id: "q19",
          type: "single-select",
          required: true,
          label: "Written content",
          options: [
            "We'll provide all copy",
            "We have rough copy that needs polishing",
            "We need help writing it from scratch",
            "A mix of both"
          ]
        },
        {
          id: "q20",
          type: "text",
          required: true,
          label: "After launch, who will be updating the website day-to-day?",
          help: "This shapes how we set up the admin experience."
        },
        {
          id: "q21",
          type: "text",
          required: false,
          label: "Are there other tools the website needs to connect with?",
          help: "Examples: Mailchimp, WhatsApp, Google Reviews, Instagram, or Razorpay."
        }
      ]
    },
    {
      id: "working",
      title: "How You Like to Work",
      intro: "This is the part most discovery forms skip. It's the part that makes a 4 to 6 month project either a pleasure or a problem.",
      questions: [
        {
          id: "q22",
          type: "text",
          required: true,
          label: "Who will be the main point of contact for this project?",
          help: "Name and role."
        },
        {
          id: "q23",
          type: "text",
          required: true,
          label: "Who else needs to approve design and content decisions?"
        },
        {
          id: "q24",
          type: "single-select",
          required: true,
          label: "How quickly can your team typically turn around feedback?",
          options: [
            "Within 24 hours",
            "2 to 3 working days",
            "Within a week",
            "It varies a lot"
          ]
        },
        {
          id: "q25",
          type: "multi-select",
          required: true,
          label: "How do you prefer to communicate during a project?",
          options: [
            "Email",
            "WhatsApp",
            "Scheduled video calls",
            "Project management tool",
            "Flexible"
          ]
        },
        {
          id: "q26",
          type: "single-select",
          required: true,
          label: "Have you worked with a design agency before?",
          options: [
            "Yes, and it went well",
            "Yes, and it didn't go well",
            "This is our first time",
            "We've worked with freelancers"
          ]
        },
        {
          id: "q27",
          type: "longtext",
          required: false,
          label: "If you've worked with someone before, what's one thing you wish they'd done differently?"
        }
      ]
    },
    {
      id: "timeline",
      title: "Timeline & Investment",
      questions: [
        {
          id: "q28",
          type: "text",
          required: true,
          label: "When would you ideally like the new site to be live?",
          help: "If there's a specific event or season tied to launch, mention it here."
        },
        {
          id: "q29",
          type: "single-select",
          required: true,
          label: "Is the timeline flexible, or is the launch date fixed?",
          options: [
            "Flexible, quality matters most",
            "Somewhat flexible",
            "Fixed deadline"
          ]
        },
        {
          id: "q30",
          type: "text",
          required: true,
          label: "What investment range have you planned for this project?",
          help: "An open figure or range is fine. This helps us shape the proposal correctly."
        },
        {
          id: "q31",
          type: "single-select",
          required: true,
          label: "How will the decision to move forward be made?",
          options: [
            "One person decides",
            "Decision shared between 2 or 3 people",
            "Needs board / partner sign-off",
            "Other"
          ],
          hasOther: true
        }
      ]
    },
    {
      id: "open",
      title: "Open Floor",
      questions: [
        {
          id: "q32",
          type: "longtext",
          required: false,
          label: "Is there anything you're nervous about, or hoping we'll handle particularly well?"
        },
        {
          id: "q33",
          type: "longtext",
          required: false,
          label: "Anything we haven't asked that you think we should know?"
        }
      ]
    }
  ]
};
